import {
  createStyles,
  ThemeOptions,
  PaletteOptions,
} from "@mui/material/styles";
import { colors } from "config/styles/themeColors";
import { fontSize, fonts } from "config/styles/fonts";

export const themeColors = () =>
  createStyles({
    palette: colors,
  }) as PaletteOptions;

export const theme = () =>
  createStyles({
    breakpoints: {
      values: {
        xs: 0,
        sm: 360,
        md: 768,
        lg: 1024,
        xl: 1200,
        xxl: 1920,
      },
    },
    typography: {
      h1: {
        fontFamily: fonts.text,
        fontWeight: 600,
        fontSize: fontSize.xxxl,
        lineHeight: "1.7",
        letterSpacing: 4,
        "@media (max-width: 1199px)": {
          fontSize: fontSize.xxl,
          letterSpacing: 0,
        },
      },
      h2: {
        fontFamily: fonts.text,
      },
      h3: {
        fontFamily: fonts.text,
      },
      h4: {
        fontFamily: fonts.text,
      },
      h5: {
        fontFamily: fonts.text,
      },
      h6: {
        fontFamily: fonts.text,
      },
      subtitle1: {
        fontFamily: fonts.text,
        fontSize: fontSize.l,
        fontWeight: 400,
        lineHeight: "1.2",
        color: colors.text.secondary,
        "@media (max-width: 1199px)": {
          fontSize: fontSize.s,
          letterSpacing: 0,
        },
      },
      caption: {
        fontFamily: fonts.text,
      },
      body1: {
        fontFamily: fonts.text,
        fontWeight: 400,
        fontSize: fontSize.m,
        letterSpacing: 0,
      },
      buttonText: {
        fontFamily: fonts.text,
        fontWeight: 500,
        fontSize: fontSize.m,
        lineHeight: "1",
        letterSpacing: 0,
      },
    },
    components: {
      MuiButtonBase: {
        defaultProps: {
          disableRipple: true,
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            padding: "0",
            width: "100%",
            height: "40px",
            borderRadius: "6px",
            fontFamily: fonts.text,
            fontWeight: 500,
            fontSize: fontSize.m,
            lineHeight: "1",
            letterSpacing: 0,
            textTransform: "none",
            border: "2px solid",
            transition: "all 0.3s ease",
            "&.primaryBlack": {
              backgroundColor: colors.text.primary,
              borderColor: colors.text.primary,
              color: colors.background.default,
              "&:hover": {
                backgroundColor: colors.gray.main,
                borderColor: colors.gray.main,
              },
              "&:focus": {
                backgroundColor: colors.text.primary,
                borderColor: colors.text.primary,
                color: colors.background.default,
              },
              "&:active": {
                backgroundColor: colors.text.primary,
                borderColor: colors.text.primary,
                color: colors.background.default,
              },
              "&:disabled": {
                borderColor: colors.gray.disabled,
                backgroundColor: colors.gray.disabled,
                color: colors.background.default,
              },
            },
            "&.secondary": {
              backgroundColor: colors.background.default,
              borderColor: colors.text.secondary,
              color: colors.text.secondary,
              "&:hover": {
                backgroundColor: colors.background.default,
                borderColor: colors.text.secondary,
                border: "2px solid",
                opacity: 0.8,
              },
              "&:active": {
                backgroundColor: colors.background.default,
                borderColor: colors.text.secondary,
                border: "2px solid",
              },
              "&:focus": {
                borderColor: colors.text.secondary,
                backgroundColor: colors.background.default,
              },
              "&:disabled": {
                borderColor: colors.gray.disabled,
                backgroundColor: colors.background.default,
                color: colors.gray.disabled,
              },
            },
            "&.error": {
              backgroundColor: colors.error.main,
              borderColor: colors.error.main,
              color: colors.background.default,
              "&:hover": {
                backgroundColor: colors.error.hover,
                borderColor: colors.error.hover,
              },
              "&:focus": {
                borderColor: colors.error.main,
                backgroundColor: colors.error.main,
              },
              "&:active": {
                color: colors.background.default,
                borderColor: colors.error.hover,
                backgroundColor: colors.error.hover,
              },
              "&:disabled": {
                borderColor: colors.gray.disabled,
                backgroundColor: colors.gray.disabled,
                color: colors.background.default,
              },
            },
            "&.success": {
              backgroundColor: colors.green.main,
              borderColor: colors.green.main,
              color: colors.background.default,
              "&:hover": {
                backgroundColor: colors.green.hover,
                borderColor: colors.green.hover,
              },
              "&:focus": {
                borderColor: colors.green.main,
                backgroundColor: colors.green.main,
              },
              "&:active": {
                color: colors.background.default,
                borderColor: colors.green.main,
                backgroundColor: colors.green.main,
              },
              "&:disabled": {
                borderColor: colors.gray.disabled,
                backgroundColor: colors.gray.disabled,
                color: colors.background.default,
              },
            },
            "&.warning": {
              backgroundColor: colors.warning.main,
              borderColor: colors.warning.main,
              color: colors.background.default,
              "&:hover": {
                backgroundColor: colors.warning.hover,
                borderColor: colors.warning.hover,
              },
              "&:focus": {
                borderColor: colors.warning.main,
                backgroundColor: colors.warning.main,
              },
              "&:active": {
                color: colors.background.default,
                borderColor: colors.warning.main,
                backgroundColor: colors.warning.main,
              },
              "&:disabled": {
                borderColor: colors.gray.disabled,
                backgroundColor: colors.gray.disabled,
                color: colors.background.default,
              },
            },
          },
        },
      },
      MuiContainer: {
        defaultProps: {
          disableGutters: true,
          maxWidth: false,
          sx: {
            px: {
              xs: "10px",
              md: "50px",
              lg: "250px",
            },
            pl: {
              md: "80px",
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            "&.MuiFormControl-root.MuiTextField-root:hover .MuiOutlinedInput-notchedOutline":
              {
                borderColor: colors.text.secondary,
              },
            "& .MuiInputBase-root": {
              width: "100%",
              height: "40px",
              padding: "10px 16px",
              borderRadius: "4px",
              color: colors.text.primary,
              lineHeight: "normal",
              "&.Mui-disabled": {
                borderColor: colors.gray.disabled,
                cursor: "not-allowed",
                backgroundColor: "inherit",
                "& .MuiInputBase-input": {
                  cursor: "not-allowed",
                },
              },
            },
            "&:hover .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
              border: "1px solid",
              borderColor: colors.gray.disabled,
            },
            "&:hover .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline":
              {
                border: "2px solid",
                borderColor: colors.error.main,
              },
            "& .MuiInputBase-root.Mui-error fieldset": {
              border: "2px solid",
              borderColor: colors.error.main,
            },
            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
              {
                border: "2px solid",
                borderColor: colors.text.secondary,
              },
            "& .MuiOutlinedInput-root.Mui-focused.Mui-error .MuiOutlinedInput-notchedOutline":
              {
                border: "2px solid",
                borderColor: colors.error.main,
              },
            "& .MuiInput-input": {
              "&::placeholder": {
                color: colors.text.secondary,
              },
              "&.Mui-disabled": {
                cursor: "not-allowed",
              },
            },
            "& .MuiInput-input.Mui-disabled ": {
              color: colors.gray.disabled,
            },
            "& .MuiFormHelperText-root": {
              marginTop: "4px",
              padding: "0",
              marginLeft: "0",
              fontFamily: fonts.text,
              fontWeight: 400,
              fontSize: fontSize.xs,
              letterSpacing: 0,
              color: colors.error.main,
            },
            "& .MuiInputAdornment-root": {
              marginLeft: "10px",
            },
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            "&:hover": {
              backgroundColor: "transparent",
            },
            "& .MuiSvgIcon-root": {
              fill: colors.text.primary,
              width: "24px",
              height: "24px",
            },
            "&.Mui-disabled .MuiSvgIcon-root": {
              fill: colors.gray.disabled,
            },
            "&.Mui-checked:hover .MuiSvgIcon-root": {
              fill: colors.accents.hover,
            },
            "&.Mui-checked > .PrivateSwitchBase-input:focus ~ .MuiSvgIcon-root > path":
              {
                outline: "2px solid",
                outlineColor: colors.text.primary,
                borderRadius: "1px",
              },
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            backgroundColor: colors.background.default,
            "&:hover": {
              backgroundColor: colors.background.secondary,
            },
            "&.Mui-selected": {
              backgroundColor: colors.background.default,
              "&:hover": {
                backgroundColor: colors.background.secondary,
              },
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            "&.MuiPaper-root": {
              "& .MuiList-root": {
                overflow: "auto",
                maxHeight: "200px",
              },
              "&.MuiDrawer-paper .MuiList-root": {
                overflow: "unset",
                maxHeight: "none",
              },
              "& .MuiList-root .MuiButtonBase-root.MuiMenuItem-root:hover": {
                backgroundColor: `${colors.accents.hover} !important`,
              },
              "& .MuiList-root .MuiButtonBase-root.Mui-selected": {
                backgroundColor: `${colors.accents.hover} !important`,
              },
            },
          },
        },
      },
    },
  }) as ThemeOptions;
